import api from "./client";

// Metrics API
export const metricsAPI = {
  getMetrics: async (clientId, metricId) => {
    const response = await api.get(`/api/v1/metrics/${metricId}`, {
      params: { client_id: clientId },
    });
    return response.data;
  },
  // Get all metrics
  getAllMetrics: async (clientId, extraParams = {}) => {
    const response = await api.get("/api/v1/metrics", {
      params: { client_id: clientId, ...extraParams },
    });
    return response.data;
  },
  // Get metrics with pagination
  getMetricsWithPagination: async (params) => {
    const response = await api.get("/api/v1/metrics", { params });
    if (response && response.data) {
      return response;
    } else {
      console.warn("Unexpected API response format:", response);
      return { data: [] };
    }
  },

  // Create a new custom metric
  createCustomMetric: async (metricData, clientId) => {
    const response = await api.post("/metrics/custom", metricData, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  // Create a new metric
  createMetric: async (metricData, clientId) => {
    const response = await api.post("/api/v1/metrics", metricData, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  // Update a custom metric
  updateMetric: async (metricId, metricData, clientId) => {
    const response = await api.put(
      `/api/v1/metrics/${metricId}`,
      metricData,
      {
        params: { client_id: clientId },
      }
    );
    return response.data;
  },

  // Delete a custom metric
  deleteMetric: async (metricId, clientId) => {
    const response = await api.delete(`/api/v1/metrics/${metricId}`, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  // Validate code metric syntax
  validateCode: async (clientId, code) => {
    const response = await api.post("/api/v1/metrics/validate_code", { client_id: clientId, code });
    return response.data;
  },

  // Test code metric against a real call
  testCode: async (clientId, code, callObservationId) => {
    const response = await api.post("/api/v1/metrics/test_code", {
      client_id: clientId,
      code,
      call_observation_id: callObservationId,
    });
    return response.data;
  },

  // Generate metrics using AI (async — enqueues background job)
  generateMetrics: async (agentId, clientId, { numberOfMetrics, focusArea } = {}) => {
    const response = await api.post("/api/v1/metrics/generate", {
      agent_id: agentId,
      client_id: clientId,
      number_of_metrics: numberOfMetrics || 1,
      additional_context: focusArea || "",
    });
    return response;
  },

  // Poll for generation status
  getGenerateStatus: async (clientId, generationId) => {
    const response = await api.get("/api/v1/metrics/generate_status", {
      params: { client_id: clientId, generation_id: generationId },
    });
    return response;
  },

  // Create generated metrics
  createGeneratedMetrics: async (metrics, clientId, agentId) => {
    const response = await api.post("/api/v1/metrics/bulk_create", {
      metrics: metrics,
      client_id: clientId,
      agent_id: agentId,
    });
    return response;
  },

  // ── Metrics Gallery (curated templates, cloned into a workspace) ──

  getGallery: async (clientId) => {
    const response = await api.get("/api/v1/metrics/gallery", {
      params: { client_id: clientId },
    });
    return response.data;
  },

  // scope: "global" | "agent"; agentIds only used when scope === "agent"
  addGalleryMetric: async (metricId, clientId, { scope, agentIds = [] } = {}) => {
    const response = await api.post(
      `/api/v1/metrics/${metricId}/add_to_workspace`,
      { scope, agent_ids: agentIds },
      { params: { client_id: clientId } },
    );
    return response.data;
  },

  // ── Prompt Override (per-agent system metric customization) ──

  getPromptOverride: async (clientId, metricId, agentId) => {
    const response = await api.get(`/api/v1/metrics/${metricId}/prompt_override`, {
      params: { client_id: clientId, agent_id: agentId },
    });
    return response.data;
  },

  updatePromptOverride: async (clientId, metricId, agentId, systemPrompt, userPromptTemplate) => {
    const response = await api.put(`/api/v1/metrics/${metricId}/prompt_override`, {
      client_id: clientId,
      agent_id: agentId,
      system_prompt: systemPrompt,
      user_prompt_template: userPromptTemplate,
    });
    return response.data;
  },

  deletePromptOverride: async (clientId, metricId, agentId) => {
    const response = await api.delete(`/api/v1/metrics/${metricId}/prompt_override`, {
      params: { client_id: clientId, agent_id: agentId },
    });
    return response.data;
  },
};

// Metric Annotations API
export const metricAnnotationsAPI = {
  create: async (clientId, data) => {
    const response = await api.post("/api/v1/metric_annotations", {
      ...data,
      client_id: clientId,
    });
    return response.data;
  },

  list: async (clientId, params = {}) => {
    const response = await api.get("/api/v1/metric_annotations", {
      params: { client_id: clientId, ...params },
    });
    return response.data;
  },

  destroy: async (clientId, id) => {
    const response = await api.delete(`/api/v1/metric_annotations/${id}`, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  resolve: async (clientId, id, note) => {
    const response = await api.patch(`/api/v1/metric_annotations/${id}/resolve`, {
      client_id: clientId,
      note,
    });
    return response.data;
  },
};

// Metric Optimizations API
export const metricOptimizationsAPI = {
  show: async (clientId, metricId, agentId) => {
    const params = { client_id: clientId };
    if (agentId) params.agent_id = agentId;
    const response = await api.get(`/api/v1/metrics/${metricId}/optimization`, { params });
    return response.data;
  },

  autoImprove: async (clientId, metricId, agentId) => {
    const response = await api.post(`/api/v1/metrics/${metricId}/optimization/auto_improve`, {
      client_id: clientId,
      agent_id: agentId,
    });
    return response.data;
  },

  reevaluate: async (clientId, metricId, agentId) => {
    const response = await api.post(`/api/v1/metrics/${metricId}/optimization/reevaluate`, {
      client_id: clientId,
      agent_id: agentId,
    });
    return response.data;
  },

  dspyTrain: async (clientId, metricId, agentId) => {
    const response = await api.post(`/api/v1/metrics/${metricId}/optimization/dspy_train`, {
      client_id: clientId,
      agent_id: agentId,
    });
    return response.data;
  },

  history: async (clientId, metricId) => {
    const response = await api.get(`/api/v1/metrics/${metricId}/optimization/history`, {
      params: { client_id: clientId },
    });
    return response.data;
  },

  updateInstructions: async (clientId, metricId, instructions, changeSummary) => {
    const response = await api.put(`/api/v1/metrics/${metricId}/optimization/update_instructions`, {
      client_id: clientId,
      instructions,
      change_summary: changeSummary,
    });
    return response.data;
  },
};
